import {useContext, useEffect, useRef, useState} from 'react';
import {Alert, Button, Typography} from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import axios from 'axios';
import {useTranslation} from 'react-i18next';
import {RelationshipDirection} from '../../../../../backend/enums/relationship-direction';
import {AuthContext} from '../../../../../contexts/auth/context';
import {useSnackBar} from '../../../../../contexts/snack-bar/context';
import {Modal} from '../../../../../components/modal/modal';
import {translations} from '../../../../../i18n/translations';
import {UsersRepository} from '../../../../../repositories/users';

export default function RemoveRelationship({seniorId}: {seniorId: string}) {
  const {state} = useContext(AuthContext);
  const session = state.session;
  const relationship = session?.selectedRelationship;
  if (
    !session ||
    !relationship ||
    relationship.withUser.id !== seniorId ||
    relationship.direction !== RelationshipDirection.CaregiverToSenior ||
    !session.relationships.some(
      (item) => item.id === relationship.id && item.withUser.id === seniorId
    )
  )
    return null;

  return (
    <RemoveDialog
      key={`${session.id}:${relationship.id}:${seniorId}`}
      userId={session.id}
      relationshipId={relationship.id}
      seniorId={seniorId}
      seniorName={relationship.withUser.name}
    />
  );
}

function RemoveDialog({
  userId,
  relationshipId,
  seniorId,
  seniorName,
}: {
  userId: string;
  relationshipId: string;
  seniorId: string;
  seniorName: string;
}) {
  const {t} = useTranslation();
  const {state, removeRelationship} = useContext(AuthContext);
  const snackBar = useSnackBar();
  const current = useRef(state.session);
  current.current = state.session;
  const alive = useRef(true);
  const pending = useRef(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<'error' | 'denied'>();

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const close = () => {
    if (!pending.current) {
      setOpen(false);
      setError(undefined);
    }
  };
  const remove = async () => {
    const session = current.current;
    const relationship = session?.selectedRelationship;
    if (
      pending.current ||
      !session ||
      session.id !== userId ||
      relationship?.id !== relationshipId ||
      relationship.withUser.id !== seniorId ||
      relationship.direction !== RelationshipDirection.CaregiverToSenior ||
      !session.relationships.some(
        (item) => item.id === relationshipId && item.withUser.id === seniorId
      )
    ) {
      close();
      return;
    }
    pending.current = true;
    setLoading(true);
    setError(undefined);
    try {
      await UsersRepository.instance.deleteUserRelationship(relationshipId);
      if (
        alive.current &&
        current.current?.id === userId &&
        current.current.selectedRelationship?.id === relationshipId
      ) {
        void snackBar.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.family_profile.remove_link_success),
        });
      }
      // Reconcile the confirmed deletion locally, including if the user switched seniors.
      // The provider checks the user before changing the current session.
      removeRelationship(relationshipId, userId);
    } catch (err) {
      if (alive.current)
        setError(axios.isAxiosError(err) && err.response?.status === 403 ? 'denied' : 'error');
    } finally {
      pending.current = false;
      if (alive.current) setLoading(false);
    }
  };

  return (
    <>
      <Button color="error" startIcon={<LinkOffIcon />} onClick={() => setOpen(true)}>
        {t(translations.family_profile.remove_link)}
      </Button>
      <Modal
        open={open}
        onClose={close}
        preventCloseClick={loading}
        title={t(translations.family_profile.remove_link)}
        actions={[
          {
            label: t(translations.family_profile.cancel),
            type: 'neutral',
            disabled: loading,
            onClick: close,
          },
          {
            label: t(translations.family_profile.remove_link),
            type: 'destructive',
            loading,
            disabled: loading,
            onClick: () => void remove(),
          },
        ]}
      >
        <Typography>
          {t(translations.family_profile.remove_link_confirm, {name: seniorName})}
        </Typography>
        {error && (
          <Alert severity="error" sx={{mt: 2}}>
            {t(
              error === 'denied'
                ? translations.family_profile.remove_link_denied
                : translations.family_profile.remove_link_error
            )}
          </Alert>
        )}
      </Modal>
    </>
  );
}
